import { useRef, useState } from 'react'
import { motion, useInView } from 'framer-motion'
import { Link } from 'react-router-dom'
import SEO from '../components/SEO'

const statsData = [
  { label: 'STR — Corpo & Fitness', value: 88, desc: 'Pratica Boxe, Judo, MMA, K1 e Krav Maga. Personal Trainer Certificato, specialista di movimento e biomeccanica.', color: '#ef4444' },
  { label: 'INT — Neurobiologia & Abitudini', value: 92, desc: 'Brain Coach Certificato, hacker dopaminergico ed esperto di bio-ottimizzazione.', color: '#00d4ff' },
  { label: 'DEX — Gaming & Pop Culture', value: 95, desc: 'Lore sterminato di RPG e anime, appassionato giocatore di WoW in gilde fantastiche.', color: '#8b5cf6' },
  { label: 'WIS — Empatia & Respirazione', value: 90, desc: 'Specialista in tecniche respiratorie per gestione stress, ansia ed emozioni.', color: '#facc15' },
]

const equipmentItems = [
  { slot: 'Testa', name: 'Cuffie Active ANC', modifier: '+15 Focus passivo contro rumori offline', rarity: 'Mitico', color: '#00d4ff' },
  { slot: 'Corpo', name: 'Cintura da Powerlifting', modifier: '+20 Resistenza a carichi di stress fisici', rarity: 'Raro', color: '#ef4444' },
  { slot: 'Mani', name: 'Tastiera Moddata 75%', modifier: '+25 Precisione di scrittura sub-quest', rarity: 'Epico', color: '#8b5cf6' },
  { slot: 'Accessorio', name: 'Caffè Espresso Doppio', modifier: '+30 Mana, attiva lo status "Iperattivo"', rarity: 'Comune', color: '#facc15' },
]

const abilities = [
  { 
    name: 'Hacker Dopaminergico', 
    type: 'Attiva (Cooldown: 24h)', 
    desc: 'Rileva istantaneamente i glitch dopaminergici quotidiani (es. scroll dei Reels) e li disattiva riorientando l\'energia sull\'azione offline.', 
    color: '#00d4ff' 
  },
  { 
    name: 'Coaching Empatico', 
    type: 'Passiva (Sempre Attiva)', 
    desc: 'Ascolta le difficoltà reali del party (i clienti bloccati) senza produrre alcun giudizio negativo. Sblocca la roadmap strategica corretta.', 
    color: '#8b5cf6' 
  },
  { 
    name: 'Respawn Fisico', 
    type: 'Attiva (Cooldown: 1h)', 
    desc: 'Usa micro-sessioni di respirazione e mobilità corporea per ripristinare il collegamento mente-corpo nei momenti di blocco profondo.', 
    color: '#ef4444' 
  }
]

const completedQuests = [
  { year: '2006', title: 'Quest I: Personal Trainer', desc: 'Inizio del percorso nel fitness e ottenimento della certificazione ufficiale come Personal Trainer, dando il via all\'allenamento del corpo offline.', color: '#ef4444' },
  { year: '2019', title: 'Quest II: Tecniche Respiratorie & Brain Coach', desc: 'Avvio dello studio e specializzazione in tecniche di respirazione, corsi di Brain Coaching e neuroscienze applicate alla gestione dello stress.', color: '#00d4ff' },
  { year: 'Oggi', title: 'Quest III: Oltre 120 Sessioni di Coaching', desc: 'Combattute e superate le battaglie reali contro i blocchi dopaminergici di gamer e professionisti digitali.', color: '#8b5cf6' },
  { year: '365d', title: 'Quest IV: Livello Scrolling "Zero"', desc: 'Eliminato al 100% lo scrolling passivo del mattino negli ultimi 365 giorni. Debuff dopaminergico sconfitto.', color: '#facc15' },
]

export default function AboutPage() {
  const profileRef = useRef(null)
  const bioRef = useRef(null)
  const abilityRef = useRef(null)
  const questRef = useRef(null)

  const profileInView = useInView(profileRef, { once: true, margin: '-50px' })
  const bioInView = useInView(bioRef, { once: true, margin: '-50px' })
  const abilityInView = useInView(abilityRef, { once: true, margin: '-50px' })
  const questInView = useInView(questRef, { once: true, margin: '-50px' })

  const [activeSlot, setActiveSlot] = useState(null)

  return (
    <div className="min-h-screen pt-32 pb-24 px-4 bg-[#050510] relative overflow-hidden">
      <SEO 
        title="Chi è Amedeo Sbardellotto | Il Founder & Nerd Coach"
        description="Scopri la scheda personaggio RPG di Amedeo: statistiche, abilità sbloccate, equipaggiamento cyberpunk e la sua storia biografica in 4 capitoli manga."
      />

      {/* Cyberpunk grid background elements */}
      <div className="absolute inset-0 pointer-events-none" style={{ backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 40px, rgba(139,92,246,0.015) 40px, rgba(139,92,246,0.015) 41px), repeating-linear-gradient(90deg, transparent, transparent 40px, rgba(139,92,246,0.015) 40px, rgba(139,92,246,0.015) 41px)' }} />

      <div className="max-w-6xl mx-auto relative z-10 space-y-16">
        
        {/* TITOLO PRINCIPALE */}
        <div className="text-center">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-[10px] text-nerd-purple font-display uppercase tracking-[0.3em] font-bold">Identità del Founder</span>
            <h1 className="font-display text-4xl sm:text-5xl font-bold text-nerd-white mt-2 mb-4">
              Amedeo Sbardellotto — <span className="text-gradient">Liv. 99</span>
            </h1>
            <p className="text-nerd-gray text-xs sm:text-sm max-w-lg mx-auto leading-relaxed">
              Founder di Nerd Coach, Personal Trainer Certificato, Brain Coach Certificato, Specialista di Movimento, Biomeccanica e Tecniche Respiratorie per la gestione dello stress e delle emozioni.
            </p>
          </motion.div>
        </div>

        {/* PARTE 1 — RPG CHARACTER SHEET (DASHBOARD) */}
        <section ref={profileRef} className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          
          {/* COLONNA SINISTRA — Foto & Equipaggiamento (5/12) */}
          <motion.div 
            className="lg:col-span-5 flex flex-col items-center gap-6"
            initial={{ opacity: 0, x: -30 }}
            animate={profileInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          >
            {/* Foto Avatar RPG */}
            <div className="relative" style={{ width: '250px' }}>
              <div className="absolute -inset-4 rounded-2xl pointer-events-none animate-pulse" style={{ background: 'radial-gradient(circle, rgba(139,92,246,0.2) 0%, transparent 75%)' }} />
              
              <div
                className="relative rounded-2xl overflow-hidden border-2"
                style={{
                  borderColor: 'rgba(139,92,246,0.5)',
                  boxShadow: '0 10px 40px rgba(139,92,246,0.3), 0 0 50px rgba(0,212,255,0.1)',
                  background: '#050510',
                }}
              >
                <div className="flex items-center justify-between px-4 py-2 bg-nerd-purple/10 border-b border-nerd-purple/20">
                  <span className="font-display text-[9px] uppercase tracking-widest text-nerd-cyan font-bold">STATUS: ATTIVO</span>
                  <span className="font-display text-[9px] uppercase tracking-widest text-nerd-gray">GUILD LEADER</span>
                </div>

                <div className="relative" style={{ height: '310px', background: '#050510' }}>
                  <img
                    src="/amedeo.jpg?v=2"
                    alt="Amedeo Sbardellotto — Nerd Coach Avatar"
                    className="w-full h-full object-cover object-top"
                    style={{ filter: 'contrast(1.04) brightness(0.96)' }}
                    onError={(e) => {
                      e.currentTarget.style.display = 'none'
                      e.currentTarget.nextSibling.style.display = 'flex'
                    }}
                  />
                  <div className="w-full h-full bg-nerd-card items-center justify-center text-nerd-gray text-sm absolute inset-0" style={{ display: 'none' }}>
                    [AVATAR FOTO]
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 h-16 pointer-events-none" style={{ background: 'linear-gradient(to top, #050510 0%, transparent 100%)' }} />
                  <div className="absolute inset-0 pointer-events-none" style={{ backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 3px, rgba(139,92,246,0.02) 3px, rgba(139,92,246,0.02) 4px)' }} />
                </div>

                <div className="px-4 py-2.5 bg-nerd-purple/5 border-t border-nerd-purple/25">
                  <div className="flex justify-between items-center">
                    <div>
                      <span className="font-display text-[10px] text-nerd-white font-bold tracking-wider block">AMEDEO</span>
                      <span className="text-[7px] text-nerd-gray uppercase tracking-widest block mt-0.5">Brain Coach · Movement Specialist</span>
                    </div>
                    <div className="text-right">
                      <span className="text-[12px] font-display font-bold text-nerd-purple">Lv. 99</span>
                    </div>
                  </div>
                </div>

                {/* Corner Decals */}
                <div className="absolute top-8 left-2 w-3.5 h-3.5 border-l-2 border-t-2 border-nerd-cyan/50" />
                <div className="absolute top-8 right-2 w-3.5 h-3.5 border-r-2 border-t-2 border-nerd-cyan/50" />
                <div className="absolute bottom-10 left-2 w-3.5 h-3.5 border-l-2 border-b-2 border-nerd-cyan/50" />
                <div className="absolute bottom-10 right-2 w-3.5 h-3.5 border-r-2 border-b-2 border-nerd-cyan/50" />
              </div>
            </div>

            {/* Equipaggiamento Cyberpunk */}
            <div className="w-full max-w-sm">
              <span className="text-[9px] text-nerd-gray uppercase tracking-widest font-display block mb-3 text-center lg:text-left">Equipaggiamento Attualmente Equipaggiato:</span>
              <div className="grid grid-cols-2 gap-3">
                {equipmentItems.map((item, idx) => (
                  <div
                    key={item.slot}
                    onMouseEnter={() => setActiveSlot(idx)}
                    onMouseLeave={() => setActiveSlot(null)}
                    className="rounded-xl p-3 border cursor-pointer select-none transition-all duration-300 relative group overflow-hidden bg-nerd-dark/45"
                    style={{
                      borderColor: activeSlot === idx ? item.color : 'rgba(139,92,246,0.12)',
                      boxShadow: activeSlot === idx ? `0 0 15px ${item.color}15` : 'none'
                    }}
                  >
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-[8px] uppercase tracking-wider text-nerd-gray font-display">Slot {item.slot}</span>
                      <span className="text-[7px] font-display uppercase tracking-widest px-1 py-0.5 rounded" style={{ color: item.color, background: `${item.color}15` }}>
                        {item.rarity}
                      </span>
                    </div>
                    <h4 className="font-display text-[10px] font-bold text-nerd-white group-hover:text-gradient transition-all">{item.name}</h4>
                    
                    {/* Tooltip info ad apparizione */}
                    <div className="text-[8px] text-nerd-gray mt-1 leading-tight font-body transition-colors group-hover:text-nerd-white">
                      {item.modifier}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>

          {/* COLONNA DESTRA — Statistiche Dynamic RPG (7/12) */}
          <motion.div 
            className="lg:col-span-7 flex flex-col justify-between self-stretch rounded-2xl bg-nerd-dark/30 border border-nerd-purple/15 p-6 md:p-8"
            initial={{ opacity: 0, x: 30 }}
            animate={profileInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="space-y-6">
              <div className="flex items-center justify-between border-b border-nerd-purple/15 pb-3">
                <div>
                  <h3 className="font-display text-xl font-bold text-nerd-white">Scheda Attributi Personaggio</h3>
                  <span className="text-[8px] text-nerd-gray uppercase tracking-widest font-display block mt-0.5">Ricalcolati in tempo reale sulla lore del founder</span>
                </div>
                <div className="w-2.5 h-2.5 rounded-full bg-nerd-cyan animate-pulse" />
              </div>

              {/* Progress bars delle Statistiche */}
              <div className="space-y-5">
                {statsData.map((stat, idx) => (
                  <div key={stat.label} className="space-y-1.5">
                    <div className="flex justify-between items-baseline text-[10px] font-display uppercase tracking-wider">
                      <span className="text-nerd-white font-bold">{stat.label}</span>
                      <span className="font-bold text-xs" style={{ color: stat.color }}>{stat.value}/100</span>
                    </div>
                    <div className="h-3 bg-nerd-dark rounded-full overflow-hidden border" style={{ borderColor: `${stat.color}15` }}>
                      <motion.div 
                        className="h-full rounded-full"
                        style={{
                          background: `linear-gradient(90deg, ${stat.color}50, ${stat.color})`,
                          boxShadow: `0 0 12px ${stat.color}40`
                        }}
                        initial={{ width: '0%' }}
                        animate={profileInView ? { width: `${stat.value}%` } : {}}
                        transition={{ type: 'spring', stiffness: 50, delay: 0.4 + idx * 0.15 }}
                      />
                    </div>
                    <p className="text-nerd-gray/80 text-[10px] leading-relaxed font-body">
                      {stat.desc}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* Informazioni di Calibrazione del Personaggio */}
            <div className="mt-8 p-4 rounded-xl border border-nerd-purple/15 bg-nerd-purple/5 relative">
              <span className="text-[8px] text-nerd-purple font-display uppercase tracking-widest block mb-1">Nota del System Log:</span>
              <p className="text-nerd-gray text-xs leading-relaxed font-body">
                Questo set di statistiche non è innato. È il risultato di un profondo processo di <strong>Respawn</strong> reale iniziato 10 anni fa. Le stesse meccaniche che Amedeo ha sperimentato su se stesso sono ora codificate all'interno di <strong>Nerd Coach</strong> per essere utilizzate sul tuo Avatar offline.
              </p>
            </div>
          </motion.div>
        </section>

        {/* PARTE 2 — BIOGRAFIA IMMERSIVA A CAPITOLI MANGA */}
        <section ref={bioRef} className="space-y-10">
          <div className="text-center">
            <span className="text-[10px] text-nerd-cyan font-display uppercase tracking-[0.3em] font-bold">Registro Storico</span>
            <h2 className="font-display text-3xl font-bold text-nerd-white mt-1 mb-2">La Biografia in 4 Capitoli</h2>
            <p className="text-nerd-gray text-xs sm:text-sm max-w-md mx-auto">
              Il viaggio evolutivo di Amedeo, dal dungeon dell'evitamento passivo fino alla liberazione del potenziale biologico offline.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            
            {/* CAPITOLO 1 */}
            <motion.div
              className="manga-panel rounded-xl p-6 relative overflow-hidden flex flex-col justify-between"
              initial={{ opacity: 0, y: 30 }}
              animate={bioInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              <div>
                <div className="flex justify-between items-baseline mb-4">
                  <span className="text-nerd-purple text-[9px] font-display uppercase tracking-widest font-bold">Capitolo 01</span>
                  <span className="text-nerd-gray/60 text-[9px] font-display uppercase">Dungeon della Timidezza</span>
                </div>
                <h3 className="font-display text-base font-bold text-nerd-white mb-3">Il Silenzio a Tavola</h3>
                <p className="text-nerd-gray text-xs md:text-sm leading-relaxed font-body">
                  Ero un bambino timidissimo, bloccato da una costante e soffocante paura del giudizio esterno. Parlare in pubblico era una minaccia di livello Boss. Ricordo le cene in cui preferivo rimanere seduto per ore in completo silenzio piuttosto che attirare l'attenzione degli sguardi degli altri. La mia safe zone iniziale era fatta di barriere e assenza totale di iniziativa reale.
                </p>
              </div>
              <div className="mt-4 pt-3 border-t border-nerd-purple/10 flex justify-between text-[8px] text-nerd-gray/40 uppercase tracking-widest font-display">
                <span>Stato: Bloccato</span>
                <span className="text-nerd-purple">STR -20 · WIS -10</span>
              </div>
            </motion.div>

            {/* CAPITOLO 2 */}
            <motion.div
              className="manga-panel rounded-xl p-6 relative overflow-hidden flex flex-col justify-between"
              initial={{ opacity: 0, y: 30 }}
              animate={bioInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <div>
                <div className="flex justify-between items-baseline mb-4">
                  <span className="text-nerd-cyan text-[9px] font-display uppercase tracking-widest font-bold">Capitolo 02</span>
                  <span className="text-nerd-gray/60 text-[9px] font-display uppercase">Il Risveglio Fisico</span>
                </div>
                <h3 className="font-display text-base font-bold text-nerd-white mb-3">Sport da Combattimento a 13 Anni</h3>
                <p className="text-nerd-gray text-xs md:text-sm leading-relaxed font-body">
                  A tredici anni ho iniziato con la Boxe, per poi ampliare il mio percorso praticando Judo, MMA, K1 e Krav Maga. È stato il mio vero punto di svolta: ho capito che il corpo non mente mai e che la fatica, il sudore ed il contatto reale sono potenti regolatori del sistema nervoso ed emotivo, gettando le fondamenta per una solida autodisciplina.
                </p>
              </div>
              <div className="mt-4 pt-3 border-t border-nerd-purple/10 flex justify-between text-[8px] text-nerd-gray/40 uppercase tracking-widest font-display">
                <span>Stato: Risveglio Fisico</span>
                <span className="text-nerd-cyan">STR +30 · WIS +20</span>
              </div>
            </motion.div>
 
            {/* CAPITOLO 3 */}
            <motion.div
              className="manga-panel rounded-xl p-6 relative overflow-hidden flex flex-col justify-between"
              initial={{ opacity: 0, y: 30 }}
              animate={bioInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <div>
                <div className="flex justify-between items-baseline mb-4">
                  <span className="text-nerd-red text-[9px] font-display uppercase tracking-widest font-bold">Capitolo 03</span>
                  <span className="text-nerd-gray/60 text-[9px] font-display uppercase">I Mondi di Silicio</span>
                </div>
                <h3 className="font-display text-base font-bold text-nerd-white mb-3">Il Rifugio Virtuale</h3>
                <p className="text-nerd-gray text-xs md:text-sm leading-relaxed font-body">
                  I videogiochi RPG complessi e le storie degli anime sono arrivati solo più tardi. Rappresentavano il rifugio perfetto ma anche un glitch dopaminergico: giocavo a WoW all\'interno di gilde fantastiche e pianificavo strategie di gruppo, ma non appena spegnevo lo schermo, la mia vita reale offline rimaneva bloccata in una safe zone immobile.
                </p>
              </div>
              <div className="mt-4 pt-3 border-t border-nerd-purple/10 flex justify-between text-[8px] text-nerd-gray/40 uppercase tracking-widest font-display">
                <span>Stato: Rifugio Virtuale</span>
                <span className="text-nerd-red">DEX +30 · INT +30</span>
              </div>
            </motion.div>
 
            {/* CAPITOLO 4 */}
            <motion.div
              className="manga-panel rounded-xl p-6 relative overflow-hidden flex flex-col justify-between"
              initial={{ opacity: 0, y: 30 }}
              animate={bioInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <div>
                <div className="flex justify-between items-baseline mb-4">
                  <span className="text-nerd-white text-[9px] font-display uppercase tracking-widest font-bold">Capitolo 04</span>
                  <span className="text-nerd-gray/60 text-[9px] font-display uppercase">La Filosofia del Percorso</span>
                </div>
                <h3 className="font-display text-base font-bold text-nerd-white mb-3">La Sintesi: Il Metodo</h3>
                <p className="text-nerd-gray text-xs md:text-sm leading-relaxed font-body">
                  Nel 2006 sono diventato Personal Trainer, iniziando ad allenare il corpo offline. Nel 2019 ho integrato lo studio delle tecniche respiratorie, delle neuroscienze e dei corsi di Brain Coach. Unendo queste discipline ho creato un metodo scientifico completo che metto al servizio dei miei allievi per guidare i player bloccati a compiere il loro respawn reale.
                </p>
              </div>
              <div className="mt-4 pt-3 border-t border-nerd-purple/10 flex justify-between text-[8px] text-nerd-gray/40 uppercase tracking-widest font-display">
                <span>Stato: Brain Coach</span>
                <span className="text-nerd-white">STR MAX · WIS MAX</span>
              </div>
            </motion.div>

          </div>
        </section>

        {/* PARTE 3 — LE ABILITÀ DEL COOPERATIVE COACHING */}
        <section ref={abilityRef} className="space-y-8">
          <div className="text-center">
            <span className="text-[10px] text-nerd-purple font-display uppercase tracking-[0.3em] font-bold">Skill Tree</span>
            <h2 className="font-display text-3xl font-bold text-nerd-white mt-1 mb-2">Abilità Sbloccate nello Skill Tree</h2>
            <p className="text-nerd-gray text-xs sm:text-sm max-w-md mx-auto">
              Queste sono le abilità attive e passive che Amedeo mette a disposizione di Nerd Coach per completare le sub-quest reali.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {abilities.map((ability, idx) => (
              <motion.div
                key={ability.name}
                className="manga-panel rounded-xl p-5 relative overflow-hidden"
                style={{ border: `1px solid ${ability.color}25` }}
                initial={{ opacity: 0, y: 30 }}
                animate={abilityInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: idx * 0.15 }}
              >
                <div 
                  className="absolute top-0 right-0 px-2 py-0.5 text-[7px] font-display rounded-bl uppercase font-semibold"
                  style={{ color: ability.color, background: `${ability.color}15`, borderLeft: `1px solid ${ability.color}20`, borderBottom: `1px solid ${ability.color}20` }}
                >
                  {ability.type}
                </div>
                
                <h3 className="font-display text-sm font-bold text-nerd-white mb-2 pt-2 flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full" style={{ background: ability.color }} />
                  {ability.name}
                </h3>
                
                <p className="text-nerd-gray text-xs leading-relaxed font-body">
                  {ability.desc}
                </p>
              </motion.div>
            ))}
          </div>
        </section>

        {/* PARTE 4 — QUEST timeline (TRAGUARDI REALI) */}
        <section ref={questRef} className="space-y-8">
          <div className="text-center">
            <span className="text-[10px] text-nerd-cyan font-display uppercase tracking-[0.3em] font-bold">Quest Log</span>
            <h2 className="font-display text-3xl font-bold text-nerd-white mt-1 mb-2">Timeline Quest Compiute</h2>
            <p className="text-nerd-gray text-xs sm:text-sm max-w-md mx-auto">
              I traguardi reali superati offline. La prova tangibile della progressione.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {completedQuests.map((quest, idx) => (
              <motion.div
                key={quest.title}
                className="manga-panel rounded-xl p-5 relative overflow-hidden flex flex-col justify-between bg-nerd-dark/20"
                style={{ border: `1px solid ${quest.color}15` }}
                initial={{ opacity: 0, y: 30 }}
                animate={questInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: idx * 0.15 }}
              >
                <div>
                  <div className="flex items-center justify-between mb-3 border-b border-nerd-purple/10 pb-1.5">
                    <span className="text-[8px] font-display text-nerd-gray uppercase">Quest Completata</span>
                    <span className="text-[10px] font-display font-bold" style={{ color: quest.color }}>{quest.year}</span>
                  </div>
                  <h3 className="font-display text-xs font-bold text-nerd-white mb-2 leading-tight">{quest.title}</h3>
                  <p className="text-nerd-gray/80 text-[10px] leading-relaxed font-body">{quest.desc}</p>
                </div>
                
                <div className="mt-4 flex items-center gap-1.5">
                  <span className="w-3.5 h-3.5 rounded bg-nerd-cyan/10 border border-nerd-cyan/35 flex items-center justify-center text-nerd-cyan text-[8px] font-bold">
                    ✓
                  </span>
                  <span className="text-[7px] text-nerd-cyan uppercase font-display font-semibold tracking-wider">Loot Sbloccato</span>
                </div>
              </motion.div>
            ))}
          </div>
        </section>

        {/* PARTE 5 — CALL TO ACTION EPICA FINALE */}
        <motion.div 
          className="manga-panel rounded-2xl p-8 md:p-10 text-center relative overflow-hidden"
          style={{
            border: '2px solid rgba(0,212,255,0.25)',
            background: 'linear-gradient(135deg, rgba(8,8,24,0.95), rgba(15,15,36,0.95))',
            boxShadow: '0 8px 32px rgba(0,212,255,0.1), 0 0 40px rgba(139,92,246,0.05)'
          }}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          {/* Corner decals */}
          <div className="absolute top-2 left-2 w-4 h-4 border-l-2 border-t-2 border-nerd-cyan/30" />
          <div className="absolute top-2 right-2 w-4 h-4 border-r-2 border-t-2 border-nerd-cyan/30" />
          <div className="absolute bottom-2 left-2 w-4 h-4 border-l-2 border-b-2 border-nerd-cyan/30" />
          <div className="absolute bottom-2 right-2 w-4 h-4 border-r-2 border-b-2 border-nerd-cyan/30" />

          <h2 className="font-display text-2xl md:text-3xl font-bold text-nerd-white mb-4">
            Unisciti alla Gilda. <span className="text-gradient">Inizia la tua Campagna.</span>
          </h2>
          
          <p className="text-nerd-gray text-xs md:text-sm max-w-lg mx-auto leading-relaxed mb-8">
            Non c'è motivo di continuare a rimandare la tua quest offline da solo. Entra nel party di Nerd Coach, ricalibra la dopamina e sblocca il livello corporeo e mentale che ti spetta nella vita reale.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/#lead-game"
              className="nerd-btn-primary text-xs tracking-wider font-semibold py-3 px-6 rounded-lg uppercase block w-full sm:w-auto"
            >
              Gioca il Libro-Game in Home 🎮
            </Link>
            
            <a
              href="https://instagram.com/nerdcoach.it"
              target="_blank"
              rel="noopener noreferrer"
              className="text-nerd-cyan text-xs font-display hover:text-nerd-blue transition-colors underline underline-offset-4 tracking-wider uppercase font-semibold"
            >
              Seguimi su Instagram →
            </a>
          </div>
        </motion.div>

      </div>
    </div>
  )
}
